import { Button, Stack, Typography } from '@mui/material';
import { navigate } from 'gatsby';
import type { MUIDataTableOptions } from 'mui-datatables';
import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import AlertButtonChip from '@components/AlertButtonChip';
import { AlertParameterChip } from '@components/Chip';
import MuiDataTable from '@components/DataTable/MuiDataTable/MuiDataTable';
import { DialogListener } from '@components/Dialog';
import IconButton from '@components/IconButton';
import AlertNotificationModalContent from '@components/Modal/Content/AlertNotificationModalContent/AlertNotificationModalContent';

import { getObservationConfig } from '@constants/fhir';
import { RECORDS } from '@constants/routes';

import { useCuration } from '@hooks/useCuration';
import { useSession } from '@hooks/useSession';

import { VitalSignObservationRecord } from '@models/vital-sign';

import { formatDate, formatName, formatVitalSign } from '@utils/format';
import { displayFriendlyRiskLevel } from '@utils/reference_range';
import { sortDates } from '@utils/sort/date';

import type { ListAlertsQuery } from 'generated/alert.generated';
import type {
  AlertRiskParameterModel,
  AlertStatus,
  ReferenceRangeRiskLevel,
  UserModel,
} from 'generated/types';

const messages = defineMessages({
  name: {
    id: 'alertTable.patientName',
    defaultMessage: 'Patient Name',
  },
  date: {
    id: 'alertTable.date',
    defaultMessage: 'Alert Date',
  },
  metric: {
    id: 'alertTable.metric',
    defaultMessage: 'Metric',
  },
  measurement: {
    id: 'alertTable.measurement',
    defaultMessage: 'Measurement',
  },
  refRange: {
    id: 'alertTable.referenceRange',
    defaultMessage: 'Reference Range',
  },
  clearAlert: {
    id: 'alertTable.clearAlert',
    defaultMessage: 'Clear Alert',
  },
  noAlerts: {
    id: 'alertTable.noAlerts',
    defaultMessage: 'No Alerts',
  },
  notifications: {
    id: 'alertTable.notifications',
    defaultMessage: 'Notifications',
  },
  alertNotifications: {
    id: 'alertTable.alertNotifications',
    defaultMessage: 'Alert Notifications',
  },
  successCount: {
    id: 'alertTable.status.successCount',
    defaultMessage: '{count} successful',
  },
  failCount: {
    id: 'alertTable.status.failCount',
    defaultMessage: '{count} failed',
  },
  notDeliveredCount: {
    id: 'alertTable.status.notDeliveredCount',
    defaultMessage: '{count} not delivered',
  },
  clearedBy: {
    id: 'alertTable.clearedBy',
    defaultMessage: 'Cleared By',
  },
});

interface RowData {
  id: string; // alert.id
  alert: { alert_id: string; status: AlertStatus };
  user?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  created_at: string; // alert.date
  metric?: {
    user_id?: string | null;
    category?: string | null; // reference_range.category
    code: string; // reference_range.code
  };
  measurement: VitalSignObservationRecord | null; // obs
  parameters?: {
    parameter_id: string; // rrc.id
    code: string; // rr.code
    risk: {
      level: ReferenceRangeRiskLevel;
      name?: string;
    };
    range: AlertRiskParameterModel[];
    type?: string | null; // rrc.code
  }[];
  notifications: {
    alert_id: string;
    success_counts?: number;
    fail_counts?: number;
    not_delivered_counts?: number;
  };
  cleared_alert_user?: Pick<
    UserModel,
    'first_name' | 'last_name' | 'user_id'
  > | null;
}

interface AlertTableProps {
  data: ListAlertsQuery['listAlerts'];
  title?: React.ReactNode;
}

export default function AlertTable({ data, title }: AlertTableProps) {
  const { formatMessage } = useIntl();
  const { viewAsPatient } = useCuration();
  const {
    state: { isOrg, isVitall, logged_in_user_id },
  } = useSession();

  const tableData = useMemo(
    () =>
      data
        .slice()
        .sort(
          (
            { status: statusA, created_at: createdA },
            { status: statusB, created_at: createdB }
          ) => {
            if (statusA === statusB) {
              return sortDates('desc')({ data: createdA }, { data: createdB });
            }
            // Descending status: (O)pen - (C)leared - (A)rchived
            return statusB.localeCompare(statusA);
          }
        )
        .map<RowData>(
          ({
            alert_id,
            cleared_alert_user,
            created_at,
            measurement,
            notification_count,
            reference_range,
            status,
            vitall_patient,
          }) => {
            const observation = measurement
              ? new VitalSignObservationRecord(measurement)
              : null;

            return {
              id: alert_id,
              created_at,
              alert: {
                alert_id,
                status,
              },
              user: vitall_patient?.user
                ? {
                    id: vitall_patient.user.user_id,
                    first_name: vitall_patient.user.first_name,
                    last_name: vitall_patient.user.last_name,
                  }
                : undefined,
              measurement: observation,
              metric: reference_range
                ? {
                    user_id: vitall_patient?.user?.user_id,
                    category: reference_range.category,
                    code: reference_range.code,
                  }
                : undefined,
              parameters: reference_range
                ? reference_range.parameters.map((parameter) => {
                    const { code, parameter_id, risk } = parameter;
                    const messageId = displayFriendlyRiskLevel(
                      reference_range.code,
                      risk
                    );

                    return {
                      parameter_id,
                      code: reference_range.code,
                      type: code,
                      risk: {
                        level: risk,
                        name: messageId
                          ? formatMessage({ id: messageId })
                          : risk,
                      },
                      range: [parameter],
                    };
                  })
                : undefined,
              notifications: {
                alert_id,
                success_counts: notification_count?.successes,
                fail_counts: notification_count?.fails,
                not_delivered_counts: notification_count?.not_delivered,
              },
              cleared_alert_user,
            };
          }
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(data)]
  );

  const handleRowClick: MUIDataTableOptions['onRowClick'] = (
    _rowData,
    { dataIndex }
  ) => {
    const { metric } = tableData[dataIndex];
    if (!metric || !metric.user_id) return null;
    if (metric.user_id !== logged_in_user_id) {
      viewAsPatient(metric.user_id);
    }
    navigate(`${RECORDS}${metric.category}/${metric.code}`);
  };

  return (
    <MuiDataTable
      columns={[
        {
          name: 'user',
          className: 'blue-hover',
          label: formatMessage(messages.name),
          options: {
            display: isOrg || isVitall,
            filter: isOrg || isVitall,
            searchable: isOrg || isVitall,
            sort: isOrg || isVitall,
            customBodyRender: (user: RowData['user']) => {
              if (!user) return null;
              const { first_name, last_name } = user;
              return formatName(first_name, last_name);
            },
          },
        },
        {
          name: 'created_at',
          className: 'date',
          label: formatMessage(messages.date),
          options: {
            filter: true,
            sort: true,
            customBodyRender: (value: RowData['created_at']) =>
              value ? formatDate({ date: value, formatter: 'datetime' }) : '-',
          },
        },
        {
          name: 'metric',
          className: 'small',
          label: formatMessage(messages.metric),
          options: {
            filter: true,
            sort: true,
            customBodyRender: (metric: RowData['metric']) => {
              if (metric?.code) {
                const signType = getObservationConfig(metric.code);
                return signType?.messageId
                  ? formatMessage({ id: signType.messageId })
                  : '-';
              }
              return null;
            },
          },
        },
        {
          name: 'measurement',
          label: formatMessage(messages.measurement),
          options: {
            customBodyRender: (measurement: RowData['measurement']) => {
              if (!measurement) return null;
              const { unit, vitallCode } = measurement;
              const value =
                typeof measurement.value === 'boolean'
                  ? null
                  : measurement.value;
              const signType = getObservationConfig(vitallCode);
              return formatVitalSign(
                { value, unit },
                true,
                signType?.format,
                signType?.multiplier
              );
            },
          },
        },
        {
          name: 'parameters',
          label: formatMessage(messages.refRange),
          options: {
            customBodyRender: (param: RowData['parameters']) => {
              if (!param) return null;
              return (
                <Stack>
                  {param.map(({ code, parameter_id, range, risk, type }) => (
                    <AlertParameterChip
                      code={code}
                      key={`${type ?? code}-${parameter_id}`}
                      range={range}
                      risk={risk.level}
                      type={type}
                    />
                  ))}
                </Stack>
              );
            },
          },
        },
        {
          name: 'notifications',
          label: formatMessage(messages.notifications),
          options: {
            display: isVitall,
            customBodyRender: (notif: RowData['notifications']) => {
              if (
                typeof notif.success_counts !== 'number' &&
                typeof notif.fail_counts !== 'number' &&
                typeof notif.not_delivered_counts !== 'number'
              ) {
                return '-';
              }

              return (
                <DialogListener
                  persistent
                  title={formatMessage(messages.alertNotifications)}
                  content={
                    <AlertNotificationModalContent alert_id={notif.alert_id} />
                  }
                >
                  {({ handleOpen }) => (
                    <Button
                      onClick={(
                        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
                      ) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleOpen();
                      }}
                      sx={{
                        textTransform: 'capitalize',
                      }}
                      variant="text"
                    >
                      <span>
                        {(notif.success_counts ?? 0) > 0 && (
                          <Typography
                            sx={{
                              color: (theme) => theme.palette.success.main,
                            }}
                            variant="subtitle2"
                          >
                            {formatMessage(messages.successCount, {
                              count: notif.success_counts,
                            })}
                          </Typography>
                        )}
                        {(notif.fail_counts ?? 0) > 0 && (
                          <Typography
                            sx={{
                              color: (theme) => theme.palette.error.main,
                            }}
                            variant="subtitle2"
                          >
                            {formatMessage(messages.failCount, {
                              count: notif.fail_counts,
                            })}
                          </Typography>
                        )}
                        {(notif.not_delivered_counts ?? 0) > 0 && (
                          <Typography
                            sx={{
                              color: (theme) => theme.palette.text.secondary,
                            }}
                            variant="subtitle2"
                          >
                            {formatMessage(messages.notDeliveredCount, {
                              count: notif.not_delivered_counts,
                            })}
                          </Typography>
                        )}
                      </span>
                    </Button>
                  )}
                </DialogListener>
              );
            },
          },
        },
        {
          name: 'alert',
          options: {
            customHeadLabelRender: () => null,
            customBodyRender: ({ alert_id, status }: RowData['alert']) => {
              return <AlertButtonChip alert_id={alert_id} status={status} />;
            },
          },
        },
        {
          name: 'cleared_alert_user',
          label: formatMessage(messages.clearedBy),
          options: {
            display: isVitall || isOrg,
            customBodyRender: (user: RowData['cleared_alert_user']) => {
              if (!user) return null;
              return formatName(user.first_name, user.last_name);
            },
          },
        },
        {
          name: 'metric',
          options: {
            customHeadLabelRender: () => null,
            customBodyRender: (metric: RowData['metric']) => {
              if (!metric) {
                return null;
              }
              const { category, code, user_id } = metric;
              return (
                <IconButton
                  color="text.secondary"
                  disabled={!user_id}
                  iconType="fad"
                  name="chevron-right"
                  onClick={() => {
                    if (!user_id) {
                      return;
                    }
                    if (user_id !== logged_in_user_id) {
                      viewAsPatient(user_id);
                    }
                    navigate(`${RECORDS}${category}/${code}`);
                  }}
                />
              );
            },
          },
        },
      ]}
      data={tableData}
      options={{
        search: true,
        sort: true,
        filter: true,
        onRowClick: handleRowClick,
        textLabels: {
          body: {
            noMatch: formatMessage(messages.noAlerts),
          },
        },
      }}
      title={title}
    />
  );
}
